import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useSafeImpersonation } from "./useSafeImpersonation";
import { useNavigate } from "react-router-dom";
import { 
  PLAN_CONFIG, 
  getResolvedFeatures,
  getUpgradeMessage,
  type PlanType, 
  type FeatureKey,
  type PlanLimits 
} from "@/config/plans";

// Server-side Limit Check Result
interface ServerLimitCheckResult {
  allowed: boolean;
  current_count: number;
  limit_value: number | null;
}

interface Company {
  id: string;
  name: string;
  plan: PlanType;
  feature_flags: Record<string, boolean>;
  limit_overrides: Record<string, number>;
  trial_started_at: string | null;
  trial_ends_at: string | null;
}

interface LimitInfo {
  limit: number | null;
  current: number;
  remaining: number | null;
  allowed: boolean;
}

interface TrialInfo {
  isActive: boolean;
  daysRemaining: number;
  endsAt: Date | null;
  isExpired: boolean;
}

// Default-Werte für nicht-geladene Zustände
const DEFAULT_LIMIT_INFO: LimitInfo = {
  limit: null,
  current: 0,
  remaining: null,
  allowed: true,
};

const DEFAULT_TRIAL_INFO: TrialInfo = {
  isActive: false,
  daysRemaining: 0,
  endsAt: null,
  isExpired: false,
};

export function usePlanAccess() {
  const { profile, loading: authLoading } = useAuth();
  const { impersonatedCompanyId, isImpersonating, isLoading: impersonationLoading, isContextAvailable } = useSafeImpersonation();
  
  // Effektive Company-ID: Impersoniert oder eigene
  const effectiveCompanyId = impersonatedCompanyId ?? profile?.company_id;
  
  // Company laden mit Error Handling
  const { data: company, isLoading: companyLoading, error: companyError } = useQuery({
    queryKey: ["company", effectiveCompanyId],
    queryFn: async () => {
      if (!effectiveCompanyId) return null;
      
      const { data, error } = await supabase
        .from("companies")
        .select("id, name, plan, feature_flags, limit_overrides, trial_started_at, trial_ends_at")
        .eq("id", effectiveCompanyId)
        .maybeSingle();
      
      if (error) {
        console.error("[usePlanAccess] Error loading company:", {
          type: "QUERY_ERROR",
          companyId: effectiveCompanyId,
          error: error.message,
        });
        throw error;
      }
      return data as Company | null;
    },
    enabled: !!effectiveCompanyId,
    staleTime: 5 * 60 * 1000, // 5 Minuten Cache
    retry: 2,
  });

  // Szenario-Count für aktuellen Monat (nur für eigene Company, da RLS)
  const { data: scenarioCountThisMonth = 0, error: scenarioCountError } = useQuery({
    queryKey: ["scenarioCountThisMonth", profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return 0;
      
      const now = new Date();
      const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      
      const { count, error } = await supabase
        .from("scenarios")
        .select("*", { count: "exact", head: true })
        .eq("company_id", profile.company_id)
        .gte("created_at", firstOfMonth);
      
      if (error) {
        console.error("[usePlanAccess] Error loading scenario count:", {
          type: "QUERY_ERROR",
          error: error.message,
        });
        return 0; // Graceful degradation
      }
      return count ?? 0;
    },
    enabled: !!profile?.company_id,
    staleTime: 60 * 1000, // 1 Minute Cache
    retry: 1,
  });

  // User-Count für Company (nur für eigene Company, da RLS)
  const { data: userCount = 0, error: userCountError } = useQuery({
    queryKey: ["userCount", profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return 0;
      
      const { count, error } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .eq("company_id", profile.company_id);
      
      if (error) {
        console.error("[usePlanAccess] Error loading user count:", {
          type: "QUERY_ERROR",
          error: error.message,
        });
        return 0; // Graceful degradation
      }
      return count ?? 0;
    },
    enabled: !!profile?.company_id,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Trial-Status berechnen
  const trialInfo = useMemo((): TrialInfo => {
    if (!company || company.plan !== "trial") {
      return { isActive: false, daysRemaining: 0, endsAt: null, isExpired: false };
    }

    if (!company.trial_ends_at) {
      return { isActive: false, daysRemaining: 0, endsAt: null, isExpired: true };
    }

    const endsAt = new Date(company.trial_ends_at);
    const now = new Date();
    const daysRemaining = Math.ceil((endsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const isActive = daysRemaining > 0;

    return {
      isActive,
      daysRemaining: Math.max(0, daysRemaining),
      endsAt,
      isExpired: !isActive,
    };
  }, [company]);

  // Effektiver Plan berechnen (Trial-Logik)
  const effectivePlan = useMemo((): PlanType => {
    if (!company) return "starter";

    const rawPlan = company.plan as PlanType;

    // Trial-Sonderlogik
    if (rawPlan === "trial") {
      // Trial aktiv → verhält sich wie Pro
      if (trialInfo.isActive) {
        return "pro";
      }
      // Trial abgelaufen → Fallback auf Starter
      return "starter";
    }

    return rawPlan;
  }, [company, trialInfo]);

  // Plan-Konfiguration
  const planConfig = PLAN_CONFIG[effectivePlan];
  const planLabel = company?.plan === "trial" 
    ? PLAN_CONFIG.trial.label 
    : planConfig.label;

  // Features für effektiven Plan
  const planFeatures = useMemo(() => {
    return getResolvedFeatures(effectivePlan);
  }, [effectivePlan]);

  /**
   * Prüft ob ein Feature verfügbar ist
   * Bei Impersonation: Zeigt Features der impersonierten Company (kein SuperAdmin-Bypass)
   */
  const hasFeature = (feature: FeatureKey): boolean => {
    // Bei Impersonation: Zeige die tatsächlichen Features der Company (kein SuperAdmin-Override)
    if (isImpersonating) {
      // Company-spezifische Feature-Flags prüfen
      if (company?.feature_flags && feature in company.feature_flags) {
        return company.feature_flags[feature];
      }
      // Plan-Features prüfen
      return planFeatures.includes(feature);
    }

    // Ohne Impersonation: Superadmin hat immer Zugriff
    if (profile?.is_superadmin) return true;

    // Company-spezifische Feature-Flags prüfen
    if (company?.feature_flags && feature in company.feature_flags) {
      return company.feature_flags[feature];
    }

    // Plan-Features prüfen
    return planFeatures.includes(feature);
  };

  /**
   * Holt ein Limit (mit Überschreibung)
   * Bei Impersonation: Zeigt Limits der impersonierten Company
   */
  const getLimit = (limitKey: keyof PlanLimits): number | null => {
    // Bei Impersonation: Zeige die tatsächlichen Limits der Company
    if (isImpersonating) {
      const rawPlan = company?.plan as PlanType | undefined;
      const limitsSource = rawPlan === "trial" && trialInfo.isActive 
        ? PLAN_CONFIG.trial.limits 
        : planConfig.limits;

      if (company?.limit_overrides && limitKey in company.limit_overrides) {
        return company.limit_overrides[limitKey];
      }
      return limitsSource[limitKey];
    }

    // Ohne Impersonation: Superadmin hat keine Limits
    if (profile?.is_superadmin) return null;

    // Trial nutzt eigene Limits, nicht die vom effectivePlan
    const rawPlan = company?.plan as PlanType | undefined;
    const limitsSource = rawPlan === "trial" && trialInfo.isActive 
      ? PLAN_CONFIG.trial.limits 
      : planConfig.limits;

    // Limit-Überschreibung prüfen
    if (company?.limit_overrides && limitKey in company.limit_overrides) {
      return company.limit_overrides[limitKey];
    }

    return limitsSource[limitKey];
  };

  /**
   * Berechnet Limit-Info für Szenarien
   */
  const scenarioLimit = useMemo((): LimitInfo => {
    const limit = getLimit("scenarios_per_month");
    const current = scenarioCountThisMonth;

    if (limit === null) {
      return { limit: null, current, remaining: null, allowed: true };
    }

    const remaining = Math.max(0, limit - current);
    return {
      limit,
      current,
      remaining,
      allowed: current < limit,
    };
  }, [scenarioCountThisMonth, company, effectivePlan, profile?.is_superadmin]);

  /**
   * Berechnet Limit-Info für Benutzer
   */
  const userLimitInfo = useMemo((): LimitInfo => {
    const limit = getLimit("users");
    const current = userCount;

    if (limit === null) {
      return { limit: null, current, remaining: null, allowed: true };
    }

    const remaining = Math.max(0, limit - current);
    return {
      limit,
      current,
      remaining,
      allowed: current < limit,
    };
  }, [userCount, company, effectivePlan, profile?.is_superadmin]);

  /**
   * Retention in Monaten
   */
  const retentionMonths = getLimit("retention_months");

  /**
   * Server-seitiger Limit-Check für Szenarien (RPC)
   * Minimum-Enforcement, serverseitig validiert
   */
  const checkCanCreateScenario = useCallback(async (): Promise<ServerLimitCheckResult> => {
    if (!company?.id) {
      return { allowed: false, current_count: 0, limit_value: 0 };
    }

    // Superadmin darf immer
    if (profile?.is_superadmin) {
      return { allowed: true, current_count: 0, limit_value: null };
    }

    const { data, error } = await supabase.rpc('can_create_scenario', {
      company_uuid: company.id
    });

    if (error) {
      console.error('Error checking scenario limit:', error);
      // Bei Fehler konservativ verweigern
      return { allowed: false, current_count: 0, limit_value: 0 };
    }

    return {
      allowed: data.allowed,
      current_count: data.current_count,
      limit_value: data.limit_value
    };
  }, [company?.id, profile?.is_superadmin]);

  /**
   * Server-seitiger Limit-Check für User (RPC)
   */
  const checkCanAddUser = useCallback(async (): Promise<ServerLimitCheckResult> => {
    if (!company?.id) {
      return { allowed: false, current_count: 0, limit_value: 0 };
    }

    if (profile?.is_superadmin) {
      return { allowed: true, current_count: 0, limit_value: null };
    }

    const { data, error } = await supabase.rpc('can_add_user', {
      company_uuid: company.id
    });

    if (error) {
      console.error('Error checking user limit:', error);
      return { allowed: false, current_count: 0, limit_value: 0 };
    }

    return {
      allowed: data.allowed,
      current_count: data.current_count,
      limit_value: data.limit_value
    };
  }, [company?.id, profile?.is_superadmin]);

  /**
   * Prüft ob ein Limit fast erreicht ist (>= 80%)
   */
  const isApproachingLimit = (current: number, limit: number | null): boolean => {
    if (limit === null) return false;
    return current / limit >= 0.8;
  };

  // Kombinierter Error-State
  const hasError = !!(companyError || scenarioCountError || userCountError);

  return {
    // Loading State - kombiniert alle Loading-States
    loading: authLoading || impersonationLoading || companyLoading,

    // Error State
    error: hasError,
    errorMessage: companyError?.message ?? scenarioCountError?.message ?? userCountError?.message ?? null,

    // Company & Plan Info
    company,
    plan: effectivePlan,
    rawPlan: (company?.plan ?? "starter") as PlanType,
    planLabel,
    planConfig,

    // Superadmin & Impersonation
    isSuperadmin: profile?.is_superadmin ?? false,
    isImpersonating,

    // Trial Info
    trial: trialInfo,

    // Feature Access
    hasFeature,
    planFeatures,

    // Limits (Client-side für UI-Anzeige)
    getLimit,
    scenarioLimit,
    userLimit: userLimitInfo,
    retentionMonths,

    // Server-side Limit Checks (für Insert-Validation)
    checkCanCreateScenario,
    checkCanAddUser,

    // Utility
    isApproachingLimit,

    // Convenience: Häufig genutzte Feature-Checks
    canCompareScenarios: () => hasFeature("scenario_comparison"),
    canUseAI: () => hasFeature("ai_analysis"),
    canUseAIContext: () => hasFeature("ai_analysis_context"),
    canExportPDF: () => hasFeature("pdf_export"),
    canExportCSV: () => hasFeature("export_csv"),
    canUseTemplates: () => hasFeature("scenario_templates_basic"),
    canUseAdvancedTemplates: () => hasFeature("scenario_templates_advanced"),
    canUseExecDashboard: () => hasFeature("exec_dashboard"),
    canUseTrendCharts: () => hasFeature("trend_charts"),
    canUseAdvancedElasticity: () => hasFeature("advanced_elasticity"),
    canUseUncertaintyAnalysis: () => hasFeature("uncertainty_analysis"),
    canUseOutcomeCalibration: () => hasFeature("outcome_calibration"),
    canUseAdvancedAssistant: () => hasFeature("scenario_assistant_advanced"),
    
    // Product Materials (Tiered)
    canUseProductMaterialsBasic: () => hasFeature("product_materials_basic"),
    canUseProductMaterialsPricing: () => hasFeature("product_materials_pricing"),
    canUseProductMaterialsSync: () => hasFeature("product_materials_sync"),
    
    // API Access
    hasAccess: (feature: string) => {
      if (feature === 'api_access') {
        return effectivePlan === 'pro' || effectivePlan === 'enterprise' || profile?.is_superadmin;
      }
      return hasFeature(feature as FeatureKey);
    },
    currentPlan: effectivePlan,

    // Upgrade-Hilfe
    getUpgradeMessage,
    
    // Limit für Szenario-Vergleich
    scenarioComparisonLimit: getLimit("scenario_comparison_limit"),
  };
}

export type { FeatureKey, PlanType, LimitInfo, TrialInfo, ServerLimitCheckResult };
